import { IBuildings, IRentsyncSettings } from '@llws/lift-entity-interfaces';
import { ActionContext } from 'vuex';
import store from '..';
import { decodeHtml } from '@/shared';
import { getNewRentsyncSetting } from '@/shared/factory/new-rentsync-setting';
import properties from '@/shared/services/properties';

export type TOverridableFields = 'name' | 'logo' | 'phone' | 'email' | 'site' | 'address';
export type TPropertyOverrideState = 'disabled' | 'global' | 'override';
export type TOverrideMethods = 'all' | 'selected';

/**
 * Override settings that sync to the `IRentsyncSettings['value']` property
 */
export type TClientListingOverrideSettings = {
  overrideMethod: TOverrideMethods,
  overrideDefaults: TOverridableFields[],
  propertyOverrides: Record<string, TOverridableFields[] | boolean>,
};

/**
 * Store state object
 */
export type TListingManagementPropertyOverrideStoreState = {
  properties: IBuildings[],
  busy: boolean,
  setting: IRentsyncSettings & { valueParsed: Partial<TClientListingOverrideSettings> },
  cleanSetting?: IRentsyncSettings,
};

/**
 * Formatted stateful object used by component table rendering.
 */
export type TPropertyOverrides = {
  name: string,
  location: string,
  disabled: boolean,
  overrides: Record<TOverridableFields, boolean>,
  overrideArray: TOverridableFields[];
};

/**
 * Override Fields as a const and sorted alphabetically.
 */
export const allOverrideFields: TOverridableFields[] = [
  'name',
  'logo',
  'phone',
  'email',
  'site',
  'address',
].sort((a, b) => (a > b ? 1 : -1)) as TOverridableFields[];

/**
 * RLS partner keys affected by the property overrides.
 */
export const overridePartnerKeys: string[] = [
  'padmapper',
  'rew',
  'rentals_infinity',
  'rent_board',
  'rent_canada',
  'toronto_rentals',
  'louer',
  'rent_faster',
];

/**
 * Default object that helps with store state initialization.
 */
export const defaultClientOverride: TClientListingOverrideSettings = {
  overrideMethod: 'selected',
  overrideDefaults: allOverrideFields,
  propertyOverrides: {},
};

/**
 * If a setting is not passed to the store's `init` action,
 * we initialize a default one using the `getNewRentsynSetting` factory function.
 * @param clientId
 * @returns
 */
const defaultSetting = (clientId?: number) => getNewRentsyncSetting(
  'company_info_override',
  JSON.stringify(defaultClientOverride),
  null,
  null,
  clientId,
);

/**
 * Calculates the value of an override. There are several cases to decide for here.
 * @param setting
 * @param overrides
 * @param propertyId
 * @param field
 * @returns
 */
export const calculateOverrideValue = (
  overrides: TClientListingOverrideSettings['propertyOverrides'],
  propertyId: number,
  field: TClientListingOverrideSettings['overrideDefaults'][0],
  setting: Partial<TClientListingOverrideSettings>,
) => {
  const propertyOverride = overrides[`${propertyId}`];

  if (typeof propertyOverride === 'boolean') {
    return propertyOverride;
  }

  if (Array.isArray(propertyOverride)) {
    return propertyOverride.includes(field);
  }

  return setting.overrideMethod === 'all' && setting.overrideDefaults?.includes(field);
};

/**
 * Default store module export to be transformed by our typed modules system
 */
export default {
  namespaced: true,

  state: {
    properties: [],
    setting: { ...defaultSetting(), valueParsed: defaultClientOverride },
    cleanSetting: undefined,
    busy: false,
  } as TListingManagementPropertyOverrideStoreState,

  getters: {
    /**
     * Transform property overrides into a formatted, compononent-compatible object that
     * can be used for switching fields on and off at the property override levelt.
     * @param state
     * @returns
     */
    propertyOverrides: (state: TListingManagementPropertyOverrideStoreState) => {
      const clientPropertyOverrides = state.setting?.valueParsed.propertyOverrides ?? {};

      return state.properties.reduce<Record<string, TPropertyOverrides>>((accProperties, curProperty) => {
        const overrides = allOverrideFields.reduce((accField, curField) => ({
          ...accField,
          [curField]: calculateOverrideValue(
            clientPropertyOverrides,
            curProperty.id ?? -1,
            curField,
            state.setting.valueParsed,
          ),
        }), {} as TPropertyOverrides['overrides']);

        return {
          ...accProperties,
          [`${curProperty.id}`]: {
            overrides,
            overrideArray: Object.keys((overrides)).filter((o) => overrides[o as TOverridableFields]) as TOverridableFields[],
            disabled: clientPropertyOverrides[`${curProperty.id}`] === false,
            name: decodeHtml(curProperty.buildingName),
            location: curProperty.city ? `${curProperty.city.cityName}, ${curProperty.city?.province?.provinceCode}` : '',
          },
        };
      }, {});
    },

    /**
     * Takes a search value to filter out property keys. These keys are used to loop through
     * the `propertyOverrides` getter on the component side.
     * @param state
     * @returns
     */
    filteredPropertyKeys: (state: TListingManagementPropertyOverrideStoreState) => (searchValue: string) => state.properties
      .filter((property) => {
        const searchCriteria = [
          property.buildingName,
          property.id,
          property.city?.cityName,
          property.city?.province?.provinceCode,
        ].join(' ').toLowerCase();

        return searchCriteria.includes(searchValue.toLowerCase());
      })
      .sort((a, b) => (a.buildingName > b.buildingName ? 1 : -1))
      .map((property) => `${property.id}`),

    /**
     * Returns client setting from store state.
     * @param state
     * @returns
     */
    clientSetting: (state: TListingManagementPropertyOverrideStoreState) => state.setting,

    /**
     * Change detection getter to inform components of changes to the `state.setting` object.
     * Uses the `state.cleanSetting` state to compare, which is only set once on intialization.
     * @param state
     * @returns
     */
    settingUpdated: (state: TListingManagementPropertyOverrideStoreState): IRentsyncSettings | undefined => {
      if (state.cleanSetting?.value !== state.setting.value) {
        const { valueParsed, ...rest } = state.setting;
        return rest;
      }

      return undefined;
    },

    busy: (state: TListingManagementPropertyOverrideStoreState) => state.busy,
  },

  mutations: {
    /**
     * Simply sets the `IBuildings` state object.
     * @param state
     * @param payload
     */
    setProperties: (state: TListingManagementPropertyOverrideStoreState, payload: IBuildings[]) => {
      state.properties = payload;
    },

    /**
     * Simply sets the `IRentsyncSettings` state object.
     * @param state
     * @param payload
     */
    setSetting: (state: TListingManagementPropertyOverrideStoreState, payload: IRentsyncSettings) => {
      state.setting = {
        ...payload,
        valueParsed: JSON.parse(payload.value ?? '{}') as Partial<TClientListingOverrideSettings>,
      };
    },

    /**
     * Set clean setting. This will be unedited and used for change detection.
     * @param state
     * @param payload
     */
    setCleanSetting: (state: TListingManagementPropertyOverrideStoreState, payload: IRentsyncSettings) => {
      state.cleanSetting = payload;
    },

    /**
     * Sets changed property overrides, using the passed `payload` to determine what property has which change applied.
     * It then turns that back into the `IRentsyncSettings['value']` compatible field.
     * @param state
     * @param payload `{ id: string, fields: TOverridableFields[] }`
     */
    setPropertyOverride: (
      state: TListingManagementPropertyOverrideStoreState,
      payload: { id: string, fields: TOverridableFields[] },
    ) => {
      const globalSetting = state.setting.valueParsed;
      const propertyOverrides = state.setting.valueParsed.propertyOverrides ?? {};

      const isFieldSetSameAsGlobal = (
        payload.fields
          .filter((f) => globalSetting.overrideDefaults?.includes(f))
          .length === globalSetting.overrideDefaults?.length
        && payload.fields.length === globalSetting.overrideDefaults?.length
      );

      if (!payload.fields.length && globalSetting.overrideMethod === 'all') {
        propertyOverrides[payload.id] = false;
      } else if (globalSetting.overrideMethod === 'all' && isFieldSetSameAsGlobal) {
        delete propertyOverrides[payload.id];
      } else {
        propertyOverrides[payload.id] = payload.fields;
      }

      globalSetting.propertyOverrides = propertyOverrides;

      state.setting = {
        ...state.setting,
        valueParsed: globalSetting,
        value: JSON.stringify(globalSetting),
      };
    },

    setBusy: (state: TListingManagementPropertyOverrideStoreState) => {
      state.busy = !state.busy;
    },
  },

  actions: {
    /**
     * Initializes store state
     * @param ctx
     * @param payload `IRentsyncSetting`
     */
    init: async (
      ctx: ActionContext<TListingManagementPropertyOverrideStoreState, typeof store.state>,
      payload?: IRentsyncSettings,
    ) => {
      const propertyRes = await properties.getPropertiesList();
      const clientSetting = payload ?? defaultSetting(ctx.getters['users/currentClient']?.id);

      ctx.commit('setProperties', propertyRes.data ?? []);
      ctx.commit('setSetting', clientSetting);
      ctx.commit('setCleanSetting', clientSetting);
    },
  },
};
